import React, { FC, useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { useGetContentQuery, useGetSharePreviewInfoQuery } from 'api';
import { useFollowed, useNotify, useProtectedClient } from 'hooks';
import { filterShareableContent } from 'features/content/helpers';
import {
  BlockListProvider,
  ContentProvider,
  SharePhotoProvider,
  SharePreviewProvider,
} from 'providers';
import { Events } from 'analytics';
import { Loaders } from 'components';
import * as paths from 'paths';

import { Footer, Form, FormBody } from '../components';

const ContentShare: FC = () => {
  const navigate = useNavigate();
  const { content_id } = useParams<{ content_id: string }>();
  const notify = useNotify();
  const { groupRules } = useFollowed();
  const { allow_sharing, id } = useProtectedClient();

  const getContentResult = useGetContentQuery(
    { content_id },
    {
      onError: notify.queryError,
    }
  );
  const getSharePreviewResult = useGetSharePreviewInfoQuery(
    { content_id },
    {
      onError: notify.queryError,
    }
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const options: { content_id: string; es_share_source?: string } = { content_id };

    if (searchParams) {
      options.es_share_source = searchParams.get('es_source');
    }

    window?.analytics?.track(Events.ShareInitiated, options);
  }, []);

  function isLoading() {
    return getContentResult.isLoading || getSharePreviewResult.isLoading;
  }

  function handleClose() {
    navigate(paths.index);
  }

  function hasData() {
    return getContentResult.data && getSharePreviewResult.data;
  }

  function hasResults() {
    if (!hasData) {
      return false;
    }

    return getContentResult.data.getContent && getSharePreviewResult.data.getSharePreviewInfo;
  }

  if (isLoading()) {
    return <Loaders.Standard />;
  }

  if (!hasData() || !hasResults()) {
    return null;
  }

  const { getContent } = getContentResult.data;
  const { getSharePreviewInfo } = getSharePreviewResult.data;

  if (!filterShareableContent(getContent, groupRules, allow_sharing)) {
    return <Navigate to={paths.index} replace />;
  }

  return (
    <ContentProvider value={getContent}>
      <SharePhotoProvider>
        <SharePreviewProvider value={getSharePreviewInfo}>
          <BlockListProvider client_id={id}>
            <Form onClose={handleClose}>
              <FormBody />
              <Footer position="fixed" />
            </Form>
          </BlockListProvider>
        </SharePreviewProvider>
      </SharePhotoProvider>
    </ContentProvider>
  );
};

export default ContentShare;
